import { useMemo } from 'react';
import { useExecutiveDashboard, ExecutiveDashboardData } from '../../api/queries';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { Users, BarChart3, Clock, Milestone, Library, Laptop, DollarSign, TrendingUp, Gauge } from 'lucide-react';

interface ExecutiveDashboardProps {
  events?: any[];
}

export default function ExecutiveDashboard({ events }: ExecutiveDashboardProps) {
  const { data, isLoading, isError, error } = useExecutiveDashboard();

  const agendaEvents = useMemo(() => data?.agenda || null, [data?.agenda]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 md:gap-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 min-h-[150px] animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {[1, 2].map(i => (
            <div key={i} className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 min-h-[280px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-[30px] p-6 text-center">
        <p className="text-sm text-rose-800 font-medium">
          Falha ao carregar o perfil executivo.
        </p>
        <p className="text-xs text-rose-600 mt-1">
          {error instanceof Error ? error.message : 'Tente novamente mais tarde.'}
        </p>
      </div>
    );
  }

  const totalEvents = agendaEvents?.totalEvents ?? events?.length ?? 0;
  const totalMeetings = agendaEvents?.totalMeetings ?? events?.filter((e: any) => e.type === 'reuniao' || e.type === 'call').length ?? 0;
  const homeOfficeCount = agendaEvents?.homeOfficeCount ?? 0;
  const absencesCount = agendaEvents?.absencesCount ?? 0;

  const categoryData = agendaEvents?.categoryBreakdown ?? [];
  const resourceData = agendaEvents?.resourceUtilization ?? [];
  const typeData = agendaEvents?.typeBreakdown ?? [];

  const totalRevenue = data.financial?.totalRevenue ?? 0;
  const totalExpenses = data.financial?.totalExpenses ?? 0;
  const profit = data.financial?.profit ?? 0;
  const margin = data.financial?.margin ?? 0;
  const financialMetrics = data.financial?.metrics ?? [];

  const totalScores = data.scores?.totalScores ?? 0;
  const latestScores = data.scores?.latest ?? [];

  const totalIndicators = data.indicators?.totalIndicators ?? 0;
  const latestIndicators = data.indicators?.latest ?? [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      {/* Primary KPIs Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          title="Compromissos Corporativos"
          value={String(totalEvents)}
          subtitle="Total de registros ativos alocados"
          icon={<Milestone size={16} className="text-[#111111]" />}
        />
        <KpiCard
          title="Reuniões / Calls"
          value={String(totalMeetings)}
          subtitle="Relações técnicas ou comerciais"
          icon={<Users size={16} className="text-[#111111]" />}
        />
        <KpiCard
          title="Home Offices Ativos"
          value={String(homeOfficeCount)}
          subtitle="Profissionais vinculados de casa"
          icon={<Laptop size={16} className="text-[#111111]" />}
        />
        <KpiCard
          title="Ausências / Afastamentos"
          value={String(absencesCount)}
          subtitle="Férias ou licenças cadastradas"
          icon={<Clock size={16} className="text-[#111111]" />}
        />
      </div>

      {/* Financial KPIs Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KpiCard
          title="Receita Total"
          value={formatCurrency(totalRevenue)}
          subtitle="Última métrica registrada"
          icon={<DollarSign size={16} className="text-[#111111]" />}
        />
        <KpiCard
          title="Despesas Totais"
          value={formatCurrency(totalExpenses)}
          subtitle="Última métrica registrada"
          icon={<TrendingUp size={16} className="text-[#111111]" />}
        />
        <KpiCard
          title="Margem de Lucro"
          value={formatPercent(margin)}
          subtitle={`Lucro: ${formatCurrency(profit)}`}
          icon={<Gauge size={16} className="text-[#111111]" />}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Category Breakdown */}
        <div className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
          <h3 className="text-sm font-bold uppercase text-[#111111] tracking-wider mb-6 flex items-center gap-2">
            <BarChart3 size={15} />
            Alocação por Setor / Categoria
          </h3>
          <div className="h-[280px] w-full flex items-center justify-center">
            {categoryData.length === 0 ? (
              <span className="text-xs text-[#64748B]">Indisponível</span>
            ) : (
              <ResponsiveContainer width="95%" height="95%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[entry.name] || '#94A3B8'} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', borderColor: '#0F172A0F', fontSize: '11px', fontFamily: 'monospace' }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconSize={10}
                    iconType="circle"
                    wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Resource Utilization */}
        <div className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
          <h3 className="text-sm font-bold uppercase text-[#111111] tracking-wider mb-6 flex items-center gap-2">
            <Library size={15} />
            Uso de Salas de Reunião e Recursos
          </h3>
          <div className="h-[280px] w-full">
            {resourceData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-[#64748B] italic">
                Nenhum recurso ou sala física reservada neste período.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={resourceData} margin={{ top: 10, right: 10, left: -25, bottom: 5 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 9, fontWeight: 'bold', fill: '#64748B' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 9, fill: '#64748B' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: '#FAFAFA' }}
                    contentStyle={{ borderRadius: '12px', borderColor: '#0F172A0F', fontSize: '11px' }}
                  />
                  <Bar dataKey="quantidade" fill="#111111" radius={[10, 10, 0, 0]} maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Financial Metrics Table */}
      {financialMetrics.length > 0 && (
        <div className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
          <h3 className="text-sm font-bold uppercase text-[#111111] tracking-wider mb-5">Métricas Financeiras</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-semibold text-[#111111]">
              <thead>
                <tr className="border-b border-[#0F172A0F] text-[#64748B] text-[10px] uppercase tracking-wider">
                  <th className="pb-3 font-bold">Métrica</th>
                  <th className="pb-3 text-right font-bold">Valor</th>
                  <th className="pb-3 text-right font-bold">Calculado em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0F172A0A]">
                {financialMetrics.map((item, idx) => (
                  <tr key={idx} className="hover:bg-[#FAFAFA]/50 transition-colors">
                    <td className="py-3 font-bold">{item.metric}</td>
                    <td className="py-3 text-right font-mono font-bold text-[#111111]">{formatCurrency(item.value)}</td>
                    <td className="py-3 text-right font-mono text-[#64748B]">
                      {new Date(item.calculatedAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scores & Indicators Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <div className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
          <h3 className="text-sm font-bold uppercase text-[#111111] tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp size={15} />
            Scores ({totalScores})
          </h3>
          {latestScores.length === 0 ? (
            <p className="text-xs text-[#64748B]">Nenhum score registrado.</p>
          ) : (
            <div className="space-y-2">
              {latestScores.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-xs border-b border-[#0F172A0F] py-2">
                  <span className="font-bold text-[#111111]">{item.scoreDefId}</span>
                  <span className="font-mono text-[#64748B]">
                    {item.value.toFixed(2)} — {item.targetType}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
          <h3 className="text-sm font-bold uppercase text-[#111111] tracking-wider mb-4 flex items-center gap-2">
            <Gauge size={15} />
            Indicadores ({totalIndicators})
          </h3>
          {latestIndicators.length === 0 ? (
            <p className="text-xs text-[#64748B]">Nenhum indicador registrado.</p>
          ) : (
            <div className="space-y-2">
              {latestIndicators.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-xs border-b border-[#0F172A0F] py-2">
                  <span className="font-bold text-[#111111]">{item.seriesId}</span>
                  <span className="font-mono text-[#64748B]">
                    {item.value.toFixed(4)} — {new Date(item.ts).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Event Types Table */}
      <div className="bg-white border border-[#0F172A0F] rounded-[30px] p-6 lg:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
        <h3 className="text-sm font-bold uppercase text-[#111111] tracking-wider mb-5">Volume por Tipo Operacional</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-semibold text-[#111111]">
            <thead>
              <tr className="border-b border-[#0F172A0F] text-[#64748B] text-[10px] uppercase tracking-wider">
                <th className="pb-3 font-bold">Tipo Operacional</th>
                <th className="pb-3 text-right font-bold">Incidências Totais</th>
                <th className="pb-3 text-right font-bold">Alocação Relativa (%)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0F172A0A]">
              {typeData.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-4 text-center text-[#64748B]">Indisponível</td>
                </tr>
              ) : (
                typeData.map((item, idx) => {
                  const percent = totalEvents > 0 ? Math.round((item.total / totalEvents) * 100) : 0;
                  return (
                    <tr key={idx} className="hover:bg-[#FAFAFA]/50 transition-colors">
                      <td className="py-3 font-bold">{item.name}</td>
                      <td className="py-3 text-right font-mono font-bold text-[#111111]">{item.total}</td>
                      <td className="py-3 text-right font-mono">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-1.5 bg-[#FAFAFA] border border-[#0F172A0F] rounded-full overflow-hidden">
                            <div className="h-full bg-[#111111]" style={{ width: `${percent}%` }} />
                          </div>
                          <span>{percent}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* Reutiliza o mapa de cores existente do componente anterior */
const CATEGORY_COLORS: Record<string, string> = {
  Projetos: '#111111',
  Comercial: '#10B981',
  Financeiro: '#8B5CF6',
  RH: '#F59E0B',
  Marketing: '#EC4899',
  Tecnologia: '#3B82F6',
  Administrativo: '#64748B',
};

function KpiCard({ title, value, subtitle, icon }: { title: string; value: string; subtitle: string; icon: React.ReactNode }) {
  return (
    <div className="bg-[#FFFFFF] border border-[#0F172A0F] rounded-[30px] p-6 flex flex-col justify-between min-h-[150px] shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
      <div className="flex justify-between items-start">
        <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-widest">{title}</span>
        <div className="w-9 h-9 rounded-full bg-[#FAFAFA] flex items-center justify-center border border-[#0F172A0D]">
          {icon}
        </div>
      </div>
      <div className="mt-4">
        <span className="text-3xl font-bold text-[#111111] tracking-tight">{value}</span>
        <span className="text-xs text-[#64748B] block mt-1">{subtitle}</span>
      </div>
    </div>
  );
}
